import React, { useState, useEffect } from "react";
import {
  Download,
  Activity,
  Megaphone,
  GitCompare,
  LayoutGrid,
  Layers,
  CheckCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Card } from "../components/ui/Card/Card";
import { Badge } from "../components/ui/Badge/Badge";
import { Text } from "../components/ui/Text/Text";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import { DisclaimerModal } from "../components/features/DisclaimerModal";
import { useCustomer } from "../contexts/CustomerContext";
import { cn } from "../lib/cn";
import { changeImpactApi } from "../services/changeImpactService";
import { whatsNewApi } from "../services/whatsNewService";
import customerServiceService from "../services/customerServiceService";
import scraperService from "../services/scraperService";
import { pdf } from "@react-pdf/renderer";
import { ReportPDFDocument } from "../components/features/reports/PDFDocument";

/**
 * GenerateReport Page
 * Generate professional PDF reports with multiple types
 */
export default function GenerateReport() {
  const { selectedCustomer } = useCustomer();
  const [selectedReport, setSelectedReport] = useState("");
  const [selectedRelease, setSelectedRelease] = useState("");
  const [availableReleases, setAvailableReleases] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // Fetch stored releases on mount
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await changeImpactApi.getStoredReleases();
        const releases = response.data.data || [];
        setAvailableReleases(releases);
        if (releases.length > 0) {
          setSelectedRelease(releases[0]); // Set latest as default
        }
      } catch (error) {
        console.error("Failed to fetch releases:", error);
      }
    };

    fetchReleases();
  }, []);

  // Report type definitions with enhanced metadata
  const reportTypes = [
    {
      id: "service-catalog",
      title: "Service Catalog",
      description:
        "Complete catalog of ALL available SAP Digital Manufacturing services across all categories (Apps/PODs, Plugins, APIs)",
      icon: LayoutGrid,
      color: "blue",
      requiresRelease: false,
      estimatedTime: "1-2 min",
      features: [
        "All SAP services (300+ services)",
        "Services by category breakdown",
        "Latest version information",
        "Complete service reference",
      ],
    },
    {
      id: "change-impact",
      title: "Change Impact Analysis",
      description:
        "Detailed analysis of service changes and deprecations that affect customer services (Changed + Announcement types)",
      icon: GitCompare,
      color: "red",
      requiresRelease: true,
      estimatedTime: "2-3 min",
      features: [
        "Severity-based impact scoring",
        "Deprecation warnings",
        "Service-specific changes",
        "Risk assessment",
      ],
    },
    {
      id: "whats-new",
      title: "What's New Features",
      description:
        "Latest features and capabilities introduced in the release (New type only), highlighting relevant updates",
      icon: Megaphone,
      color: "green",
      requiresRelease: true,
      estimatedTime: "2-3 min",
      features: [
        "New features & capabilities",
        "Service relevance highlighting",
        "Release highlights",
        "Documentation links",
      ],
    },
    {
      id: "service-usage",
      title: "Service Usage Report",
      description:
        "Simple report listing services currently assigned to this customer with version information",
      icon: Activity,
      color: "purple",
      requiresRelease: false,
      estimatedTime: "1 min",
      features: [
        "Customer's assigned services only",
        "Current version information",
        "Service usage summary",
        "Quick reference",
      ],
    },
    {
      id: "combined",
      title: "Comprehensive Report",
      description:
        "All-in-one report combining service usage and change impact analysis for comprehensive customer insights",
      icon: Layers,
      color: "indigo",
      requiresRelease: true,
      estimatedTime: "2-3 min",
      features: [
        "Service usage overview",
        "Change impact analysis",
        "Severity breakdown",
        "Complete customer insights",
      ],
    },
  ];

  const getColorClasses = (color, isSelected) => {
    const colors = {
      blue: {
        bg: isSelected ? "bg-blue-50" : "bg-white",
        border: isSelected ? "border-blue-500" : "border-gray-200",
        icon: isSelected
          ? "bg-blue-100 text-blue-600"
          : "bg-blue-50 text-blue-500",
        text: "text-blue-600",
      },
      red: {
        bg: isSelected ? "bg-red-50" : "bg-white",
        border: isSelected ? "border-red-500" : "border-gray-200",
        icon: isSelected ? "bg-red-100 text-red-600" : "bg-red-50 text-red-500",
        text: "text-red-600",
      },
      green: {
        bg: isSelected ? "bg-green-50" : "bg-white",
        border: isSelected ? "border-green-500" : "border-gray-200",
        icon: isSelected
          ? "bg-green-100 text-green-600"
          : "bg-green-50 text-green-500",
        text: "text-green-600",
      },
      purple: {
        bg: isSelected ? "bg-purple-50" : "bg-white",
        border: isSelected ? "border-purple-500" : "border-gray-200",
        icon: isSelected
          ? "bg-purple-100 text-purple-600"
          : "bg-purple-50 text-purple-500",
        text: "text-purple-600",
      },
      indigo: {
        bg: isSelected ? "bg-indigo-50" : "bg-white",
        border: isSelected ? "border-indigo-500" : "border-gray-200",
        icon: isSelected
          ? "bg-indigo-100 text-indigo-600"
          : "bg-indigo-50 text-indigo-500",
        text: "text-indigo-600",
      },
    };
    return colors[color] || colors.blue;
  };

  // Helper function to fetch customer image and convert to base64 for PDF embedding
  const fetchCustomerImageBase64 = async (customerId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const token = localStorage.getItem("cia_token");

      // Use backend proxy to avoid CORS issues and ensure proper content-type
      const response = await fetch(
        `${API_URL}/api/customers/image-base64/${customerId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      // Get the blob from binary response
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Detect actual image type from file signature (magic bytes)
      let mimeType = "image/jpeg"; // default
      if (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) {
        mimeType = "image/jpeg"; // JPEG: FF D8
      } else if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50) {
        mimeType = "image/png"; // PNG: 89 50 4E 47
      }

      // Create blob with correct MIME type
      const blob = new Blob([arrayBuffer], { type: mimeType });

      // Convert blob to base64 using FileReader
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Failed to fetch customer image:", error);
      return null;
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedReport || !selectedCustomer) {
      console.error("Please select a report type");
      return;
    }

    const reportType = reportTypes.find((r) => r.id === selectedReport);

    // Validate release version for reports that require it
    if (reportType.requiresRelease && !selectedRelease) {
      console.error("Please select a release version for this report type");
      return;
    }

    setIsGenerating(true);

    try {
      // Step 1: Fetch data from backend APIs

      // Fetch services based on report type
      let services;
      if (selectedReport === "service-catalog") {
        // Fetch ALL services from catalog
        const catalogData = await scraperService.fetchServiceCatalog();
        // Flatten the catalog structure into a single array
        services = [
          ...catalogData.data.apps_and_pods,
          ...catalogData.data.plugins,
          ...catalogData.data.pod2_plugins,
          ...catalogData.data.apis,
        ];
      } else {
        // Fetch customer-specific services for other report types
        services = await customerServiceService.getCustomerServices(
          selectedCustomer.id
        );
      }

      let reportData = {
        services,
        impacts: null,
        whatsNew: null,
      };

      // Fetch change impact data if needed
      if (selectedReport === "change-impact" || selectedReport === "combined") {
        const impactResponse = await changeImpactApi.analyzeChangeImpact(
          selectedCustomer.id,
          selectedRelease
        );
        reportData.impacts = impactResponse.data.data;
      }

      // Fetch what's new data if needed (NOT included in combined report)
      if (selectedReport === "whats-new") {
        const whatsNewResponse = await whatsNewApi.analyzeWhatsNew(
          selectedRelease
        );
        reportData.whatsNew = whatsNewResponse.data.data;
      }

      // Step 2: Fetch customer logo as base64 via backend proxy if it exists
      let customerWithBase64Logo = { ...selectedCustomer };
      if (selectedCustomer.imageUrl) {
        try {
          const base64Image = await fetchCustomerImageBase64(
            selectedCustomer.id
          );
          if (base64Image) {
            customerWithBase64Logo.imageUrl = base64Image;
          } else {
            console.warn(
              "Image conversion returned null, PDF will not include logo"
            );
          }
        } catch (error) {
          console.error("Failed to convert image:", error);
        }
      }

      // Step 3: Generate PDF using react-pdf

      const pdfDoc = (
        <ReportPDFDocument
          reportType={selectedReport}
          customer={customerWithBase64Logo}
          services={reportData.services}
          impacts={reportData.impacts}
          whatsNew={reportData.whatsNew}
          releaseVersion={selectedRelease}
        />
      );

      // Generate blob
      const blob = await pdf(pdfDoc).toBlob();

      // Create download link
      const filename = `${selectedCustomer.name.replace(
        /\s+/g,
        "_"
      )}_${reportType.title.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Get dynamic description based on selected report
  const getPageDescription = () => {
    if (!selectedReport) {
      return `Create professional PDF reports for ${
        selectedCustomer?.name || "your customer"
      }`;
    }
    const reportType = reportTypes.find((r) => r.id === selectedReport);
    if (
      reportType?.id === "service-catalog" ||
      reportType?.id === "whats-new"
    ) {
      return "Create professional PDF reports with SAP service information";
    }
    return `Create professional PDF reports for ${
      selectedCustomer?.name || "your customer"
    }`;
  };

  return (
    <PageContainer title="Generate Reports" description={getPageDescription()}>
      <div className="space-y-8">
        {/* Report Type Selection */}
        <section>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const isSelected = selectedReport === report.id;
              const colorClasses = getColorClasses(report.color, isSelected);

              return (
                <Card
                  key={report.id}
                  className={cn(
                    "p-5 cursor-pointer transition-all hover:shadow-lg border-2",
                    colorClasses.bg,
                    colorClasses.border
                  )}
                  onClick={() => setSelectedReport(report.id)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className={cn("p-2.5 rounded-lg", colorClasses.icon)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    {isSelected && (
                      <CheckCircle
                        className={cn("h-5 w-5", colorClasses.text)}
                      />
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {report.title}
                  </h3>

                  {/* Description */}
                  <Text className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {report.description}
                  </Text>

                  {/* Features */}
                  <ul className="space-y-1 mb-3">
                    {report.features.slice(0, 2).map((feature, index) => (
                      <li
                        key={index}
                        className="text-xs text-gray-500 flex items-center"
                      >
                        <span className="w-1 h-1 bg-gray-400 rounded-full mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Metadata */}
                  <div className="flex items-center justify-end pt-3 border-t border-gray-100">
                    <div className="flex gap-1.5">
                      <Badge variant="outline" className="text-xs">
                        PDF
                      </Badge>
                      {report.requiresRelease && (
                        <Badge variant="outline" className="text-xs">
                          Release
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Generate Button & Messages */}
        {selectedReport && (
          <section className="flex flex-col gap-4">
            {/* Disclaimer Notice */}
            <Card className="mb-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-amber-900 mb-1">
                    Important Disclaimer
                  </p>
                  <p className="text-amber-800">
                    Generated reports depend on accurate service configuration.{" "}
                    <button
                      onClick={() => setShowDisclaimerModal(true)}
                      className="underline font-medium hover:text-amber-900"
                    >
                      Read full disclaimer
                    </button>
                  </p>
                </div>
              </div>
            </Card>

            <Card className="">
              <div className="space-y-4">
                {/* Top: Release Selection & Buttons */}
                <div className="flex items-center justify-between gap-6">
                  {/* Left: Release Selection (if needed) */}
                  {reportTypes.find((r) => r.id === selectedReport)
                    ?.requiresRelease && (
                    <div className="flex-1 max-w-xs">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Release Version
                      </label>
                      <select
                        value={selectedRelease}
                        onChange={(e) => setSelectedRelease(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        {availableReleases.length === 0 ? (
                          <option value="">Loading releases...</option>
                        ) : (
                          availableReleases.map((release) => (
                            <option key={release} value={release}>
                              {release}{" "}
                              {availableReleases.indexOf(release) === 0
                                ? "(Latest)"
                                : ""}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  )}

                  {/* Right: Generate & Clear Buttons */}
                  <div className="flex gap-3 ml-auto">
                    <button
                      onClick={handleGenerateReport}
                      disabled={isGenerating || !selectedReport}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-[200px]"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Generate PDF
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setSelectedReport("")}
                      disabled={isGenerating}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {/* Bottom: Info Text */}
                <div className="pt-4 border-t border-gray-200">
                  <Text className="text-sm text-gray-600">
                    {reportTypes.find((r) => r.id === selectedReport)?.id ===
                    "service-catalog"
                      ? "This report will generate a comprehensive PDF catalog of all available SAP Digital Manufacturing services with detailed descriptions and categorization."
                      : reportTypes.find((r) => r.id === selectedReport)?.id ===
                        "whats-new"
                      ? "This report highlights new features and capabilities introduced in the selected release version, providing insights into the latest SAP updates."
                      : reportTypes.find((r) => r.id === selectedReport)?.id ===
                        "change-impact"
                      ? "This report analyzes service changes and their potential impact on your customer's current implementation, categorized by severity level."
                      : reportTypes.find((r) => r.id === selectedReport)?.id ===
                        "service-usage"
                      ? "This report provides a snapshot of all services currently assigned to the customer with version information and usage details."
                      : "This comprehensive report combines service usage with change impact analysis to provide complete visibility into your customer's SAP environment."}
                  </Text>
                </div>
              </div>
            </Card>
          </section>
        )}
      </div>

      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
      />
    </PageContainer>
  );
}
